import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, DAY_KEYS, FORMAT_BADGE } from "@/lib/api";
import { useClient } from "@/context/ClientContext";

export default function GoalsDialog({ onSaved }) {
  const { client, reload } = useClient();
  const [open, setOpen] = useState(false);
  const [goals, setGoals] = useState({ videos: 0, photos: 0, posts: 0 });
  const [rhythm, setRhythm] = useState([]);
  const [notes, setNotes] = useState("");
  const [template, setTemplate] = useState("");

  useEffect(() => {
    if (!open || !client) return;
    setGoals(client.monthly_goals || { videos: 0, photos: 0, posts: 0 });
    setRhythm(client.posting_rhythm || []);
    setNotes((client.notes || []).join("\n"));
    setTemplate(client.caption_template || "");
  }, [open, client]);

  const save = async () => {
    try {
      const sorted = [...rhythm].sort((a, b) => DAY_KEYS.indexOf(a.day) - DAY_KEYS.indexOf(b.day));
      await api.put(`/clients/${client.id}`, { monthly_goals: goals, posting_rhythm: sorted, notes: notes.split("\n").map((n) => n.trim()).filter(Boolean), caption_template: template });
      await reload();
      onSaved?.();
      setOpen(false);
      toast.success("Gespeichert");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = (d) => setRhythm((x) => (x.find((r) => r.day === d) ? x.filter((r) => r.day !== d) : [...x, { day: d, format: "video", purpose: "", time: "18:00" }]));
  const upd = (d, k, v) => setRhythm((x) => x.map((r) => (r.day === d ? { ...r, [k]: v } : r)));
  const sorted = [...rhythm].sort((a, b) => DAY_KEYS.indexOf(a.day) - DAY_KEYS.indexOf(b.day));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="edit-goals-button"><Settings2 className="h-4 w-4" />Ziele & Rhythmus</Button>
      </DialogTrigger>
      <DialogContent className="dark text-foreground max-w-2xl max-h-[90vh] overflow-y-auto scroll-thin">
        <DialogHeader><DialogTitle>Ziele & Rhythmus · {client?.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {[["videos", "Videos"], ["photos", "Fotos"], ["posts", "Posts gesamt"]].map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label>{l} / Monat</Label>
              <Input type="number" min={0} value={goals[k] ?? 0} onChange={(e) => setGoals({ ...goals, [k]: parseInt(e.target.value || "0", 10) })} data-testid={`goal-input-${k}`} />
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <Label>Posting-Rhythmus</Label>
          <div className="flex gap-1.5">
            {DAY_KEYS.map((d) => (
              <button key={d} type="button" onClick={() => toggle(d)} className={`h-9 w-9 rounded-md text-xs transition-colors ${rhythm.find((r) => r.day === d) ? "bg-teal-500 text-slate-950 font-semibold" : "bg-white/5 text-muted-foreground hover:bg-white/10"}`} data-testid={`posting-day-${d}`}>{d}</button>
            ))}
          </div>
          {sorted.map((r) => (
            <div key={r.day} className="grid grid-cols-[40px_auto_80px_1fr] gap-2 items-center" data-testid={`rhythm-edit-${r.day}`}>
              <span className="text-sm font-semibold">{r.day}</span>
              <div className="flex gap-1">
                {Object.entries(FORMAT_BADGE).map(([k, f]) => (
                  <button key={k} type="button" onClick={() => upd(r.day, "format", k)} className={`text-[11px] px-2 py-1 rounded-full transition-colors ${r.format === k ? f.cls : "bg-white/5 text-slate-400"}`} data-testid={`rhythm-format-${r.day}-${k}`}>{f.label}</button>
                ))}
              </div>
              <Input type="time" value={r.time} onChange={(e) => upd(r.day, "time", e.target.value)} className="h-8 text-xs" data-testid={`rhythm-time-${r.day}`} />
              <Input value={r.purpose} onChange={(e) => upd(r.day, "purpose", e.target.value)} placeholder="Zweck, z.B. Reichweite – Tipp" className="h-8 text-xs" data-testid={`rhythm-purpose-${r.day}`} />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <Label>Text-Vorlage für Captions (Agent übernimmt Aufbau & Stil)</Label>
          <Textarea rows={6} value={template} onChange={(e) => setTemplate(e.target.value)} placeholder={"HOOK: Kurze, provokante erste Zeile\n\nTEXT: 2–3 Sätze fachlicher Mehrwert, ehrlich, mit Beispiel\n\nCTA: Klare Aufforderung, z.B. „Schreib uns …“"} data-testid="caption-template-input" />
        </div>
        <div className="space-y-2">
          <Label>Regeln für den Agenten (eine pro Zeile)</Label>
          <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="client-notes-input" />
        </div>
        <Button onClick={save} className="bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="save-goals-button">Speichern</Button>
      </DialogContent>
    </Dialog>
  );
}
